// The states DESIGN.md requires of Review that the mockups do not draw: a
// Chapter locked by its own render, a Refinement that failed, Chapter Audio
// the script has moved past, and an older version on screen.

import type { ChapterReview, ScriptVersionSummary } from "@contract";
import { useTranslation } from "react-i18next";
import { Button } from "../../../components/control/Button";
import { Banner } from "../../../components/feedback/Banner";
import { ProgressBar } from "../../../components/feedback/ProgressBar";
import { DateTime } from "../../../components/format/DateTime";

export function ReviewBanners({
  chapter,
  viewing,
  stopping,
  retrying,
  restoring,
  onStopRender,
  onRetry,
  onBackToCurrent,
  onRestoreViewed,
}: {
  chapter: ChapterReview;
  /** The version on screen when it is not the current one. */
  viewing: ScriptVersionSummary | null;
  /** One flag per call, so a restore in flight does not spin the Stop button. */
  stopping: boolean;
  retrying: boolean;
  restoring: boolean;
  /** Opens the confirmation; stopping a render is never a single click. */
  onStopRender: () => void;
  onRetry: (jobId: string) => void;
  onBackToCurrent: () => void;
  onRestoreViewed: (version: ScriptVersionSummary) => void;
}) {
  const { t } = useTranslation(["review", "errors"]);
  const job = chapter.activeJob;
  return (
    <>
      {chapter.lock ? (
        <Banner
          tone="warn"
          label={t("review:locked.label")}
          action={
            <Button variant="secondary" size="sm" busy={stopping} onClick={onStopRender}>
              {t("review:locked.stop")}
            </Button>
          }
        >
          {t("review:locked.body")}
        </Banner>
      ) : null}

      {chapter.state === "failed" && chapter.failure ? (
        <Banner
          tone="danger"
          // Which stage gave up. A failed Refinement and a failed render read
          // very differently to the Author — one has no script, the other has a
          // good one underneath — and the failure key itself is stage-agnostic,
          // so the label is taken from the job that stopped. Spelled out rather
          // than templated, so the reference scan sees all three keys.
          label={
            job?.kind === "render"
              ? t("review:failed.render")
              : job?.kind === "refine"
                ? t("review:failed.refinement")
                : t("review:failed.label")
          }
          action={
            job?.manualRetryRequired ? (
              <Button variant="secondary" size="sm" busy={retrying} onClick={() => onRetry(job.id)}>
                {t("review:failed.retry")}
              </Button>
            ) : null
          }
        >
          {/* The failure says which stage gave up and why; the screen must not
              guess, because a Chapter can fail in Refinement or in the Render
              and the two read very differently to the Author. */}
          {t(`errors:${chapter.failure.key}`)}
        </Banner>
      ) : null}

      {chapter.state === "refining" ? (
        <Banner tone="working" label={t("review:refining.label")}>
          {job?.progress ? (
            <ProgressBar
              value={job.progress.total === 0 ? 0 : job.progress.done / job.progress.total}
              label={t("review:refining.body")}
            />
          ) : (
            t("review:refining.body")
          )}
        </Banner>
      ) : null}

      {chapter.audio?.stale === true ? (
        <Banner tone="warn" label={t("review:stale.label")}>
          {t("review:stale.body")}
        </Banner>
      ) : null}

      {viewing ? (
        <Banner
          tone="info"
          label={t("review:viewing.label")}
          action={
            <>
              <Button variant="quiet" size="sm" onClick={onBackToCurrent}>
                {t("review:viewing.back")}
              </Button>
              <Button
                variant="secondary"
                size="sm"
                busy={restoring}
                disabled={chapter.lock !== null}
                onClick={() => onRestoreViewed(viewing)}
              >
                {t("review:version.restore")}
              </Button>
            </>
          }
        >
          {t("review:viewing.body", { number: viewing.number })}{" "}
          <DateTime value={viewing.createdAt} style="dateTime" />
        </Banner>
      ) : null}
    </>
  );
}
